// use-timeline-csv.ts
"use client"

import { useCallback, useMemo } from "react"
import { format } from "date-fns"
import type { CandidateTimelineItemV1 } from "openapi/api/model"

type CsvRow = Record<string, string | number | boolean | null | undefined>

interface UseTimelineCsvOptions {
  /** Filename without extension. Defaults to candidate_timeline_<yyyy-MM-dd_HH-mm> */
  filenameBase?: string
  /** Optional custom filename builder */
  filename?: (now: Date) => string
  /** If true, strips HTML from comment bodies (recommended). Default true. */
  stripHtmlBodies?: boolean
  /** If true, sorts items newest -> oldest by occurred_at before exporting. Default true. */
  sortDescending?: boolean
}

function defaultFilename(now: Date) {
  try {
    return `candidate_timeline_${format(now, "yyyy-MM-dd_HH-mm")}.csv`
  } catch {
    return `candidate_timeline_export.csv`
  }
}

function stripHtml(html?: string | null) {
  if (!html) return ""
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim()
}

function csvEscape(value: unknown) {
  const s = value === null || value === undefined ? "" : String(value)
  const needsQuotes = /[",\n\r]/.test(s)
  const escaped = s.replace(/"/g, '""')
  return needsQuotes ? `"${escaped}"` : escaped
}

function toCsv(rows: CsvRow[]) {
  if (rows.length === 0) return ""

  const preferredCols = [
    "id",
    "type",
    "title",
    "subtitle",
    "occurred_at",
    "occurred_at_local",
    "job_id",
    "job_title",
    "company_name",
    // comment
    "comment_label",
    "comment_body_text",
    // task
    "task_status",
    "task_priority",
    "task_due_date",
    "task_description",
    // stage transition
    "from_stage",
    "to_stage",
    // calendar event
    "event_type",
    "event_start_at",
    "event_end_at",
    "event_location",
    "event_notes",
    // outreach
    "outreach_channel",
    "external_thread_url",
  ]

  const allKeysSet = new Set<string>()
  for (const r of rows) Object.keys(r).forEach((k) => allKeysSet.add(k))

  const allKeys = Array.from(allKeysSet)
  const keys = [
    ...preferredCols.filter((k) => allKeysSet.has(k)),
    ...allKeys.filter((k) => !preferredCols.includes(k)).sort(),
  ]

  const header = keys.map(csvEscape).join(",")
  const lines = rows.map((r) => keys.map((k) => csvEscape(r[k])).join(","))
  return [header, ...lines].join("\n")
}

function downloadCsv(csvText: string, filename: string) {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function formatDateLocal(dateString?: string | null) {
  if (!dateString) return ""
  try {
    return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a")
  } catch {
    return dateString
  }
}

function buildRows(items: CandidateTimelineItemV1[], opts: Required<Pick<UseTimelineCsvOptions, "stripHtmlBodies">>) {
  return items.map((item) => {
    const details: any = item.details ?? {}

    const bodyText =
      opts.stripHtmlBodies && typeof details.body === "string" ? stripHtml(details.body) : (details.body ?? "")

    return {
      id: item.id ?? "",
      type: item.type ?? "",
      title: item.title ?? "",
      subtitle: item.subtitle ?? "",
      occurred_at: item.occurred_at ?? "",
      occurred_at_local: formatDateLocal(item.occurred_at),

      job_id: (item as any).job_id ?? "",
      job_title: (item as any).job_title ?? "",
      company_name: (item as any).company_name ?? "",

      comment_label: details.label ?? "",
      comment_body_text: bodyText,

      task_status: details.status ?? "",
      task_priority: details.priority ?? "",
      task_due_date: details.due_date ?? "",
      task_description: details.description ?? "",

      from_stage: details.from_stage ?? "",
      to_stage: details.to_stage ?? "",

      event_type: details.event_type ?? "",
      event_start_at: details.start_at ?? "",
      event_end_at: details.end_at ?? "",
      event_location: details.location ?? "",
      event_notes: details.notes ?? "",

      outreach_channel: details.channel ?? "",
      external_thread_url: details.external_thread_url ?? "",
    } satisfies CsvRow
  })
}

export function useTimelineCsv(items: CandidateTimelineItemV1[], options?: UseTimelineCsvOptions) {
  const opts = useMemo(() => {
    return {
      filenameBase: options?.filenameBase ?? "",
      filename: options?.filename,
      stripHtmlBodies: options?.stripHtmlBodies ?? true,
      sortDescending: options?.sortDescending ?? true,
    }
  }, [options?.filenameBase, options?.filename, options?.stripHtmlBodies, options?.sortDescending])

  const csvText = useMemo(() => {
    const safeItems = Array.isArray(items) ? items : []
    const sorted = opts.sortDescending
      ? [...safeItems].sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime())
      : safeItems

    const rows = buildRows(sorted, { stripHtmlBodies: opts.stripHtmlBodies })
    return toCsv(rows)
  }, [items, opts.sortDescending, opts.stripHtmlBodies])

  const exportCsv = useCallback(() => {
    const now = new Date()
    const filename =
      opts.filename?.(now) ??
      (opts.filenameBase ? `${opts.filenameBase}.csv` : defaultFilename(now))

    downloadCsv(csvText, filename)
  }, [csvText, opts.filename, opts.filenameBase])

  return {
    csvText,
    exportCsv,
    rowCount: items?.length ?? 0,
    hasData: (items?.length ?? 0) > 0,
  }
}
